import * as React from 'react';
import type { JSX } from 'react';
import { DropdownPopover, Outside, Spinner } from '@cian/ui-kit';

import { SUGGEST_PROP_DEFAULTS } from '../../internal/constants';
import { useIconMargin } from '../../internal/hooks/useIconMargin';
import { useMaxLinesCssVarable } from '../../internal/hooks/useMaxLinesCssVarable';
import { useSuggestMouseEvents } from '../../internal/hooks/useSuggestMouseEvents';
import { useSuggestSelectedItem } from '../../internal/hooks/useSuggestSelectedItem';
import { SuggestDesktopList } from '../../internal/components/SuggestDesktopList/SuggestDesktopList';
import { SuggestionDesktopDefaultItem } from '../../internal/components/SuggestionDesktopDefaultItem/SuggestionDesktopDefaultItem';
import { SuggestEmptyListContent } from '../../internal/components/SuggestEmptyListContent/SuggestEmptyListContent';
import { SuggestionTextItem } from '../../internal/components/SuggestionTextItem/SuggestionTextItem';
import { isEmptyListPlainText } from '../../internal/utils/isEmptyListPlainText';
import { SuggestionTitleItem } from '../../internal/components/SuggestionTitleItem/SuggestionTitleItem';
import { TextHighlighter } from '../../internal/components/TextHighlighter/TextHighlighter';
import { ISuggestionItem, ISuggestDesktopProps, TSuggestionItemType } from '../../types';
import styles from './styles.module.css';

const suggestItemComponentMap: Record<
  TSuggestionItemType,
  React.FC<React.ComponentProps<typeof SuggestionTitleItem | typeof SuggestionDesktopDefaultItem>>
> = { default: SuggestionDesktopDefaultItem, title: SuggestionTitleItem };

export const SuggestDesktop = <T,>(props: ISuggestDesktopProps<T>): JSX.Element => {
  const { onSelect, onClose } = props;
  const listBoxRef = React.useRef<HTMLUListElement>(null);
  const suggestionsChildrenRef = React.useRef<HTMLDivElement>(null);

  useMaxLinesCssVarable({ ref: listBoxRef, maxLines: props.maxLines, open: props.open });
  const { setSelectedItemIndex, selectedItemIndex } = useSuggestSelectedItem(props, {
    suggestionsChildrenRef,
    listBoxRef,
  });
  const mouseEventProps = useSuggestMouseEvents(setSelectedItemIndex);
  const { getIconMarginForItem, setTitleItemRefByIndex } = useIconMargin(props.suggestions);

  const onItemClick = React.useCallback(
    (item: ISuggestionItem<T>, index: number) => () => {
      setSelectedItemIndex(index);
      onSelect?.(item, { source: 'click' });
    },
    [setSelectedItemIndex, onSelect],
  );
  const onOutsideClick = React.useCallback(() => onClose?.({ source: 'outsideClick' }), [onClose]);
  const insideRefs = React.useMemo(() => [listBoxRef, suggestionsChildrenRef], []);

  const emptyListContent = props.emptyListContent;
  const hasEmptyListContent = Boolean(emptyListContent);
  const emptyListPlainText = hasEmptyListContent && isEmptyListPlainText(emptyListContent);
  const emptyListCustomContent = hasEmptyListContent && !isEmptyListPlainText(emptyListContent);
  const isSuggestionsEmpty = props.suggestions.length === 0;
  const isEmptyListLoadingShown = isSuggestionsEmpty && Boolean(props.emptyListLoading);

  const isPopupListShown =
    props.suggestions.length > 0 || isEmptyListLoadingShown || (isSuggestionsEmpty && hasEmptyListContent);

  return (
    <DropdownPopover
      open={props.open}
      sameWidth="width"
      content={
        isPopupListShown && (
          <Outside onOutside={onOutsideClick} insideRefs={insideRefs}>
            <div>
              {isEmptyListLoadingShown && (
                <SuggestEmptyListContent
                  variant="desktop"
                  listHeight={props.maxHeight ?? SUGGEST_PROP_DEFAULTS.maxHeight}
                >
                  <div className={styles['loader-wrap']}>
                    <Spinner size={24} />
                  </div>
                </SuggestEmptyListContent>
              )}
              {isSuggestionsEmpty && !props.emptyListLoading && emptyListCustomContent && (
                <SuggestEmptyListContent
                  variant="desktop"
                  listHeight={props.maxHeight ?? SUGGEST_PROP_DEFAULTS.maxHeight}
                >
                  {emptyListContent}
                </SuggestEmptyListContent>
              )}
              {(props.suggestions.length > 0 ||
                (isSuggestionsEmpty && !props.emptyListLoading && emptyListPlainText)) && (
                <SuggestDesktopList ref={listBoxRef} listHeight={props.maxHeight ?? SUGGEST_PROP_DEFAULTS.maxHeight}>
                  {isSuggestionsEmpty && !props.emptyListLoading && emptyListPlainText && (
                    <SuggestionTextItem>{emptyListContent}</SuggestionTextItem>
                  )}
                  {props.suggestions.map((suggestion, i) => {
                    const ItemComponent = suggestItemComponentMap[suggestion.type ?? SUGGEST_PROP_DEFAULTS.itemType];

                    return (
                      <ItemComponent
                        {...suggestion}
                        key={i}
                        type={suggestion.type}
                        iconMargin={getIconMarginForItem(suggestion)}
                        ref={suggestion.type === 'title' ? setTitleItemRefByIndex(i) : undefined}
                        onMouseMove={mouseEventProps.onMouseMove(i)}
                        onMouseLeave={mouseEventProps.onMouseLeave}
                        onMouseEnter={mouseEventProps.onMouseEnter}
                        isSelected={selectedItemIndex === i}
                        onClick={onItemClick(suggestion, i)}
                        variant="desktop"
                      >
                        <TextHighlighter highlightValue={props.highlightValue}>{suggestion.title}</TextHighlighter>
                      </ItemComponent>
                    );
                  })}
                </SuggestDesktopList>
              )}
            </div>
          </Outside>
        )
      }
    >
      <div ref={suggestionsChildrenRef} data-testid="suggest-children">
        {props.children}
      </div>
    </DropdownPopover>
  );
};

SuggestDesktop.displayName = 'SuggestDesktop';
